// Book a delivery into a stock cupboard: the delivery note (number and scan) and a
// line per batch received — what it is, its batch number and expiry, how many.
// POST /stock/receive; the rules are core/stock.receive.
//
// The same batch received twice tops up the one already held rather than making a
// second, so a batch keeps one history however it arrived. Drugs must have a batch
// number (that is what a recall notice names); Medical stock may not have one.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, MenuItem, Stack, TextField, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../../config";
import { notify } from "../../notify";
import { BATCHED_CATEGORIES, typeLabel } from "../../constants";
import { containerPath, isStrictCategory, roleOf } from "../../stockRules";
import StockDocumentField from "./StockDocumentField";

const blankLine = (key) => ({ key, type_id: null, batch_number: "", expiry_date: "", quantity: "" });

export default function ReceiveStockDialog({ open, onClose, onDone, items, types }) {
  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const stores = items.filter(i => roleOf(i, typeById) === "store");
  const stockTypes = types.filter(t => BATCHED_CATEGORIES.includes(t.category))
    .sort((a, b) => a.name.localeCompare(b.name));

  const [containerId, setContainerId] = useState("");
  const [reference, setReference] = useState("");
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([blankLine(1)]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContainerId(stores.length === 1 ? stores[0].id : "");
    setReference(""); setDoc(null); setNotes(""); setLines([blankLine(1)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (key, p) => setLines(ls => ls.map(l => (l.key === key ? { ...l, ...p } : l)));
  const addLine = () => setLines(ls => [...ls, blankLine(Math.max(...ls.map(l => l.key)) + 1)]);
  const removeLine = (key) => setLines(ls => ls.filter(l => l.key !== key));

  const lineProblem = (l) => {
    const t = typeById[l.type_id];
    if (!t) return "Choose what was received";
    if (!(Number(l.quantity) >= 1)) return "How many?";
    if (isStrictCategory(t.category) && !l.batch_number.trim()) return "Drugs need a batch number";
    return null;
  };
  const filled = lines.filter(l => l.type_id || l.quantity || l.batch_number);
  const problems = filled.map(lineProblem).filter(Boolean);
  const canSave = containerId && filled.length > 0 && problems.length === 0 && !saving;

  const save = () => {
    setSaving(true);
    axios.post(`${API_BASE_URL}/stock/receive`, {
      container_id: containerId,
      reference: reference.trim() || null,
      document_id: doc?.id || null,
      notes: notes.trim() || null,
      lines: filled.map(l => ({
        type_id: l.type_id,
        batch_number: l.batch_number.trim() || null,
        expiry_date: l.expiry_date || null,
        quantity: Number(l.quantity),
      })),
    }).then(() => {
      notify("Delivery booked in.", "success");
      onDone && onDone();
      onClose();
    }).catch(() => {}).finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Receive a delivery</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Book drugs or medical stock into a stock cupboard. Each batch gets its own
            line — the batch number and expiry are printed on the pack.
          </DialogContentText>
          {stores.length === 0 ? (
            <Alert severity="warning">
              No container is a stock cupboard yet. Set the stock role on the
              cupboard's type, on the Types page, first.
            </Alert>
          ) : (
            <TextField label="Into" select fullWidth value={containerId}
                       onChange={e => setContainerId(e.target.value)}>
              {stores.map(s => (
                <MenuItem key={s.id} value={s.id}>{containerPath(s, itemById, typeById)}</MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
            <TextField label="Delivery note number" value={reference} sx={{ flex: 1 }}
                       onChange={e => setReference(e.target.value)} />
            <StockDocumentField category="delivery_note" label="Attach delivery note"
                                value={doc} onChange={setDoc} />
          </Stack>

          {lines.map(l => {
            const t = typeById[l.type_id];
            const strict = t && isStrictCategory(t.category);
            return (
              <Stack key={l.key} direction={{ xs: "column", sm: "row" }} spacing={1}
                     sx={{ alignItems: { sm: "flex-start" } }}>
                <Autocomplete
                  sx={{ flex: 2, minWidth: 220 }}
                  options={stockTypes}
                  getOptionLabel={o => `${typeLabel(o)} (${o.category})`}
                  value={t || null}
                  onChange={(_, v) => patch(l.key, { type_id: v ? v.id : null })}
                  renderInput={p => <TextField {...p} label="Item" size="small" />} />
                <TextField label="Batch number" size="small" sx={{ flex: 1 }}
                           required={strict} value={l.batch_number}
                           error={strict && filled.includes(l) && !l.batch_number.trim()}
                           onChange={e => patch(l.key, { batch_number: e.target.value })} />
                <TextField label="Expiry date" type="date" size="small" sx={{ width: 170 }}
                           slotProps={{ inputLabel: { shrink: true } }} value={l.expiry_date}
                           onChange={e => patch(l.key, { expiry_date: e.target.value })} />
                <TextField label="Quantity" type="number" size="small" sx={{ width: 100 }}
                           slotProps={{ htmlInput: { min: 1, step: 1 } }} value={l.quantity}
                           onChange={e => patch(l.key, { quantity: e.target.value })} />
                <Tooltip title="Remove line">
                  <span>
                    <IconButton size="small" disabled={lines.length === 1}
                                onClick={() => removeLine(l.key)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          })}
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          </Box>
          <TextField label="Notes" fullWidth multiline value={notes}
                     onChange={e => setNotes(e.target.value)} />
          {problems.length > 0 && filled.length > 0 && (
            <Alert severity="info">{[...new Set(problems)].join(" · ")}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>Book in</Button>
      </DialogActions>
    </Dialog>
  );
}
